import { normalize_text } from './view_case_details_core_helpers';

const TIMESTAMP_FIELDS = ['timestamp', 'date_created', 'created_at'];
const ACTION_FIELDS = ['action_taken', 'action', 'transition_display', 'event', 'log_type'];
const DETAIL_FIELDS = ['details', 'message', 'note', 'description'];
const ROUTED_TO_FIELDS = [
  'routed_to',
  'assigned_to',
  'assigned_to_name',
  'assignee',
  'assignee_name',
  'owner',
  'handler',
  'target_user',
  'target_user_name',
];
const ACTOR_FIELDS = [
  'actor',
  'user_full_name',
  'reviewer',
  'reviewer_name',
  'user_role',
  'role',
  'role_name',
];
const APPLICATION_MATCH_FIELDS = ['application', 'application_id', 'details', 'message', 'note'];

function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function first_field_value(record = {}, fields = []) {
  return first_present(fields.map((field) => record?.[field]));
}

function unique_values(values = []) {
  return values.filter(Boolean).filter((value, index, rows) => rows.indexOf(value) === index);
}

function timestamp_source(log = {}) {
  return first_field_value(log, TIMESTAMP_FIELDS);
}

export function format_audit_timestamp(value) {
  if (!value) return '';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? normalize_text(value) : `${date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
  })} ${date.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
}

export function audit_timestamp_value(log = {}) {
  const date = new Date(timestamp_source(log));
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

export function status_name(status) {
  return status ? normalize_text(status?.name || status?.status || status) : '';
}

function transition_status(log = {}) {
  const from_status = status_name(log?.from_status || log?.from_status_label);
  const to_status = status_name(log?.to_status || log?.to_status_label);
  return from_status && to_status ? `${from_status} -> ${to_status}` : '';
}

function audit_action(log = {}) {
  return normalize_text(first_field_value(log, ACTION_FIELDS) || 'Audit event');
}

function audit_actor(log = {}) {
  return normalize_text(first_field_value(log, ACTOR_FIELDS) || 'SYSTEM').toUpperCase();
}

function nested_field_value(record = {}, field = '') {
  const details = record?.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    return details[field];
  }

  return undefined;
}

function routed_to_detail(log = {}) {
  const direct_value = first_field_value(log, ROUTED_TO_FIELDS);
  const nested_value = first_present(ROUTED_TO_FIELDS.map((field) => nested_field_value(log, field)));
  const routed_to = normalize_text(direct_value || nested_value);

  return routed_to ? `Routed to ${routed_to}` : '';
}

function audit_event(log = {}) {
  return unique_values([
    audit_action(log),
    normalize_text(log?.status) || transition_status(log),
    normalize_text(first_field_value(log, DETAIL_FIELDS)),
    routed_to_detail(log),
  ]).join(' - ');
}

export function normalize_audit_row(log = {}, index = 0) {
  const action = audit_action(log);

  return {
    id: log?.id || `${action}-${index}`,
    when: normalize_text(log?.when || format_audit_timestamp(timestamp_source(log))),
    actor: audit_actor(log),
    event: audit_event(log),
    sort_time: audit_timestamp_value(log),
    raw: log,
  };
}

function application_haystack(log = {}) {
  return normalize_text(APPLICATION_MATCH_FIELDS.map((field) => log?.[field] || '').join(' ')).toLowerCase();
}

function application_match_terms(application_id) {
  return [
    String(application_id).toLowerCase(),
    `application id: ${application_id}`.toLowerCase(),
    `c-${application_id}`.toLowerCase(),
  ];
}

export function audit_log_matches_application(log = {}, application_id = null) {
  const haystack = application_haystack(log);
  return !application_id || application_match_terms(application_id).some((term) => haystack.includes(term));
}
